import { SxProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker as MuiDatePicker, DatePickerProps as MuiDatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { DateValidationError } from "@mui/x-date-pickers/internals/hooks/validation/useDateValidation";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { red, grey } from "designSystem/styles/color";
import TextField from "designSystem/components/textfield/TextField";
import { isInvalidDate } from "csi/utility/dateUtility";
import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { defaultDateFormat, useLocale } from "designSystem/locale";
import { textfieldStyle } from "designSystem/styles/style-functions";

const PREFIX = "DatePicker";

const classes = {
	input: `${PREFIX}input`,
	inputPadding: `${PREFIX}inputPadding`,
	errorInput: `${PREFIX}errorInput`,
	noErrorInput: `${PREFIX}noErrorInput`,
};

// replace the char with the same two chars
const _replaceChar = (format: string, char: string): string => {
	if ((format.match(new RegExp(char, "g")) || []).length == 1) {
		format = format.replace(char, char + char);
	}

	return format;;
};

/** Convert the month and day to two digits as needed so that it satisfy the component */
export const formatDateTime = (format: string): string => {
	// handle month
	format = _replaceChar(format, "M");

	// handle day
	format = _replaceChar(format, "d");

	// handle hour
	format = _replaceChar(format, "H");
	format = _replaceChar(format, "h");

	return format;
};

const StyledTextField = styled(TextField)(({
	theme,
}) => ({
	[`& .${classes.input}`]: textfieldStyle(theme),
	[`& .${classes.inputPadding}.${classes.inputPadding}`]: {
		paddingRight: 14,
	},
	[`& .${classes.errorInput}`]: {
		borderColor: `${red[600]} !important`,
	},
	[`& .${classes.noErrorInput}`]: {
		borderColor: `${grey[400]} !important`,
	},
}));

export interface DatePickerProps extends Partial<Omit<MuiDatePickerProps<Date, Date>, "format" | "value" | "onChange">> {
	/** indicates selected date is invalid to affect styling of component */
	invalid?: boolean;

	/** onChange callback */
	onChange?: (value: Date) => void;

	/** Not to display red border around date for invalid date value */
	noRedBorderOnError?: boolean;

	/** sx styles for input */
	inputSx?: SxProps;

	/** If the current value is invalid, determine whether the date picker reverts back to the previous valid date when onBlur*/
	revertToPreviousValue?: boolean;

	/** date value */
	value?: Date;
}

const DatePicker: FunctionComponent<DatePickerProps> = (props) => {
	const { invalid, noRedBorderOnError, onError, onChange, revertToPreviousValue, value, ...otherProps } = props;
	const [error, setError] = useState<DateValidationError>(null);
	const [selectedDate, setSelectedDate] = useState<Date>(value || null);
	const [previousDate, setPreviousDate] = useState<Date>(value || null);
	const loc = useLocale();
	const dateFormat = formatDateTime(loc.getDatePattern() || defaultDateFormat);
	const displayError = (invalid || error) && !noRedBorderOnError;
	const ref = useRef(null);

	const handleError = (reason: DateValidationError, errorValue: Date | string) => {
		// do not track error if invalid because the current value and initial value are empty
		if (reason === "invalidDate" && !errorValue && !value) {
			return;
		}

		setError(reason);
		onError?.(reason, errorValue instanceof Date ? errorValue : null);
	};

	const handleChange = (value: Date): void => {
		if (isInvalidDate(value)) {
			handleError("invalidDate", value);
		} else {
			setError(null);
			onChange?.(value);
		}
		setSelectedDate(value);
	};

	function onBlur(): void {
		if (invalid) {
			setSelectedDate(previousDate);
			onChange?.(previousDate);
			setError(null);
		}
	};

	useEffect(() => {
		const dateInvalid = ref.current.firstChild.ariaInvalid;
		if (dateInvalid === "false" && selectedDate !== null) {
			setPreviousDate(selectedDate);
		}
	}, [selectedDate]);

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<MuiDatePicker
				DialogProps={{
					className: "csi-date-picker-popper",
				}}
				InputProps={{
					className: `${classes.input} ${displayError ? classes.errorInput : ""} ${noRedBorderOnError ? classes.noErrorInput : ""} ${classes.inputPadding}`,
					sx: props.inputSx,
					onBlur: revertToPreviousValue ? onBlur : null,
					ref,
				}}
				PopperProps={{
					className: "csi-date-picker-popper",
					placement: "bottom-start",
				}}
				inputFormat={dateFormat}
				onChange={handleChange}
				onError={handleError}
				renderInput={(props) => <StyledTextField {...props} />}
				value={selectedDate}
				{...otherProps}
			/>
		</LocalizationProvider>
	);
};

export default DatePicker;